import React, { useEffect, useState} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,

  Stack,
  Typography,
  Button,
  IconButton,
  Avatar,
  TextField,
  Switch,
  Chip,
  CircularProgress,
  Tooltip,
  InputAdornment,

} from "@mui/material";
import {
  FaArrowLeft,
  FaEdit,
  FaCheck,
  FaTimes,
  FaCamera,
  FaSignature,
  FaSchool,
  FaKey,
 
  FaExclamationTriangle,
  FaUser,
  FaMapMarkerAlt,
  FaAtlas,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaSave,
} from "react-icons/fa";
import { API_URL } from "../config";
import ProfileImage from "../assets/images/profile.png";
import LoadingPage from "../components/loading";

/* ══════════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════════ */
const T = {
  blue: "#1565C0",
  blueLight: "#1976D2",
  blueMid: "#42A5F5",
  blueGlass: "rgba(21,101,192,0.08)",
  purple: "#7B3FF2",
  purpleGlass: "rgba(123,63,242,0.08)",
  text: "#0D1117",
  textSub: "#5A6478",
  textMuted: "#9BA3B2",
  surface: "rgba(255,255,255,0.7)",
  surfaceStrong: "rgba(255,255,255,0.9)",
  border: "rgba(255,255,255,0.5)",
  borderBlue: "rgba(21,101,192,0.15)",
  success: "#059669",
  error: "#DC2626",
  font: "'DM Sans', sans-serif",
};

/* ══════════════════════════════════════════
   ANIMATION VARIANTS
══════════════════════════════════════════ */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};


const slideDown = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.25, ease: "easeIn" },
  },
};

/* ══════════════════════════════════════════
   GLASS CARD WRAPPER
══════════════════════════════════════════ */
const GlassCard = ({ children, sx = {}, ...props }) => (
  <Box
    sx={{
      background: "rgba(255,255,255,0.72)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: "1px solid rgba(255,255,255,0.55)",
      borderRadius: "20px",
      boxShadow:
        "0 4px 24px rgba(21,101,192,0.07), 0 1px 3px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8)",
      overflow: "hidden",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

/* ══════════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════════ */
const SectionHeader = ({ icon, color = T.blue, title, subtitle, action }) => (
  <Box
    sx={{
      px: 3,
      py: 2.5,
      borderBottom: "1px solid rgba(0,0,0,0.05)",
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${color}, ${color}cc)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 4px 12px ${color}40`,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: T.text,
            fontSize: "0.95rem",
            fontFamily: T.font,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: "0.75rem", color: T.textMuted, fontFamily: T.font }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Stack>
    {action}
  </Box>
);

/* ══════════════════════════════════════════
   FIELD LABEL
══════════════════════════════════════════ */
const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.68rem",
      fontWeight: 700,
      color: T.textMuted,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      mb: 0.75,
      fontFamily: T.font,
    }}
  >
    {children}
  </Typography>
);

/* ══════════════════════════════════════════
   STYLED INPUT
══════════════════════════════════════════ */
const GlassInput = ({
  value,
  placeholder,
  onChange,
  disabled,
  type = "text",
  icon,
  accentColor = T.blue,
  endAdornment,
}) => (
  <TextField
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    disabled={disabled}
    type={type}
    size="small"
    fullWidth
    InputProps={{
      startAdornment: icon ? (
        <InputAdornment position="start">
          <Box sx={{ color: disabled ? T.textMuted : accentColor, fontSize: 13, display: "flex" }}>
            {icon}
          </Box>
        </InputAdornment>
      ) : undefined,
      endAdornment,
    }}
    sx={{
      "& .MuiOutlinedInput-root": {
        color: T.text,
        borderRadius: "12px",
        background: disabled
          ? "rgba(245,247,250,0.8)"
          : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(8px)",
        fontFamily: T.font,
        fontSize: "0.88rem",
        transition: "all 0.2s ease",
        "& fieldset": {
          borderColor: "rgba(0,0,0,0.08)",
          transition: "border-color 0.2s ease",
        },
        "&:hover fieldset": {
          borderColor: disabled ? "rgba(0,0,0,0.08)" : `${accentColor}55`,
        },
        "&.Mui-focused fieldset": {
          borderColor: accentColor,
          borderWidth: "1.5px",
        },
        "&.Mui-disabled": {
          background: "rgba(245,247,250,0.8)",
        },
      },
      "& input::placeholder": { color: T.textMuted, fontSize: "0.85rem" },
      "& input:disabled": {
        color: T.textSub,
        WebkitTextFillColor: T.textSub,
      },
    }}
  />
);

/* ══════════════════════════════════════════
   PILL BUTTON
══════════════════════════════════════════ */
const PillBtn = ({
  children,
  onClick,
  variant = "filled",
  color = T.blue,
  icon,
  disabled,
  loading,
  size = "small",
}) => {
  const isFilled = variant === "filled";
  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      size={size}
      startIcon={
        loading ? (
          <CircularProgress size={12} sx={{ color: isFilled ? "#fff" : color }} />
        ) : icon ? (
          <Box sx={{ fontSize: 12, display: "flex" }}>{icon}</Box>
        ) : undefined
      }
      sx={{
        borderRadius: "100px",
        textTransform: "none",
        fontWeight: 600,
        fontSize: size === "small" ? "0.78rem" : "0.85rem",
        px: size === "small" ? 2.5 : 3,
        py: size === "small" ? 0.8 : 1,
        fontFamily: T.font,
        background: isFilled
          ? `linear-gradient(135deg, ${color}, ${color}bb)`
          : "transparent",
        color: isFilled ? "#fff" : color,
        border: isFilled ? "none" : `1.5px solid ${color}44`,
        boxShadow: isFilled ? `0 4px 14px ${color}35` : "none",
        "&:hover": {
          background: isFilled
            ? `linear-gradient(135deg, ${color}ee, ${color}99)`
            : `${color}0f`,
          boxShadow: isFilled ? `0 6px 18px ${color}40` : "none",
          borderColor: isFilled ? undefined : `${color}88`,
          transform: "translateY(-1px)",
        },
        "&:active": { transform: "translateY(0)" },
        "&:disabled": { opacity: 0.5, transform: "none" },
        transition: "all 0.2s cubic-bezier(0.34,1.56,0.64,1)",
      }}
    >
      {loading ? (size === "small" ? "Saving…" : "Updating…") : children}
    </Button>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const InstProfilePage = () => {
  const navigate = useNavigate();

  const [profile, setProfile]                   = useState(null);
  const [username, setUsername]                 = useState("");
  const [name, setName]                         = useState("");
  const [address, setAddress]                   = useState("");
  const [code, setCode]                         = useState("");
  const [isInst, setIsInst]                     = useState(false);
  const [profilePicFile, setProfilePicFile]     = useState(null);
  const [signFile, setSignFile]                 = useState(null);
  const [picPreview, setPicPreview]             = useState("");
  const [signPreview, setSignPreview]           = useState("");
  const [isRemovingBg, setIsRemovingBg]         = useState(false);
  const [editMode, setEditMode]                 = useState(false);
  const [changePasswordMode, setChangePasswordMode] = useState(false);
  const [oldPassword, setOldPassword]           = useState("");
  const [newPassword, setNewPassword]           = useState("");
  const [showOld, setShowOld]                   = useState(false);
  const [showNew, setShowNew]                   = useState(false);
  const [isLoading, setIsLoading]               = useState(false);
  const [alert, setAlert]                       = useState({ type: "", message: "" });
  const [avatarHover, setAvatarHover]           = useState(false);

  const token = JSON.parse(localStorage.getItem("authToken") || "{}");

  /* auto-dismiss alert */
  useEffect(() => {
    if (alert.message) {
      const t = setTimeout(() => setAlert({}), 4000);
      return () => clearTimeout(t);
    }
  }, [alert.message]);

  useEffect(() => {
    if (!token?.access) { navigate("/login"); return; }
    axios
      .get(`${API_URL}/student/profile/`, {
        headers: { Authorization: `Bearer ${token.access}` },
      })
      .then(({ data }) => {
        setProfile(data);
        setCode(data.student_code || "N/A");
        setUsername(data.user?.username || "");
        setName(data.name || "");
        setAddress(data.address || "");
        setIsInst(!!data.is_inst);
        setPicPreview(data.profile_pic || ProfileImage);
        setSignPreview(data.signature || "");
      })
      .catch(() => setAlert({ type: "error", message: "Failed to load profile." }));
  }, [navigate, token?.access]);

  const removeBackground = async (file) => {
    const formData = new FormData();
    formData.append("image_file", file);
    formData.append("size", "auto");
    const res = await fetch("https://api.remove.bg/v1.0/removebg", {
      method: "POST",
      headers: { "X-Api-Key": "hxRA6YNfZzrnsXgMYv9ZBYCh" },
      body: formData,
    });
    if (!res.ok) throw new Error("Background removal failed");
    return res.blob();
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfilePicFile(file);
    setPicPreview(URL.createObjectURL(file));
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsRemovingBg(true);
    try {
      const cleaned = await removeBackground(file);
      setSignFile(cleaned);
      setSignPreview(URL.createObjectURL(cleaned));
    } catch {
      setSignFile(file);
      setSignPreview(URL.createObjectURL(file));
    } finally {
      setIsRemovingBg(false);
    }
  };

  const handleSave = async () => {
    if (!username.trim() || !name.trim() || !address.trim()) {
      setAlert({ type: "error", message: "All fields are required." });
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("username", username.trim());
    formData.append("name", name.trim());
    formData.append("address", address.trim());
    formData.append("is_inst", isInst);
    formData.append("country", profile.country);
    if (profilePicFile) formData.append("profile_pic", profilePicFile);
    if (signFile) formData.append("signature", signFile);
    try {
      await axios.put(`${API_URL}/student/profile/`, formData, {
        headers: {
          Authorization: `Bearer ${token.access}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setAlert({ type: "success", message: "Profile updated successfully!" });
      setEditMode(false);
    } catch {
      setAlert({ type: "error", message: "Failed to update profile." });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!oldPassword || !newPassword) {
      setAlert({ type: "error", message: "Both passwords are required." });
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/change-password/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.access}`,
        },
        body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
      });
      if (res.ok) {
        setAlert({ type: "success", message: "Password changed successfully!" });
        setChangePasswordMode(false);
        setOldPassword("");
        setNewPassword("");
      } else {
        setAlert({ type: "error", message: "Incorrect current password." });
      }
    } catch {
      setAlert({ type: "error", message: "Network error." });
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (n) =>
    n.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  if (!profile) return <LoadingPage />;

  return (
    <>
      {/* Google Font injection */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');`}</style>

      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(145deg, #EBF4FF 0%, #F5F0FF 40%, #E8F4FF 70%, #F0EBFF 100%)",
          py: { xs: 3, md: 5 },
          px: { xs: 2, sm: 3, md: 4 },
          fontFamily: T.font,
          position: "relative",
          overflowX: "hidden",
        }}
      >
        {/* Background decorative blobs */}
        <Box
          sx={{
            position: "fixed",
            top: -120,
            right: -120,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(21,101,192,0.08) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "fixed",
            bottom: -100,
            left: -100,
            width: 350,
            height: 350,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(123,63,242,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <Box
          sx={{ maxWidth: 700, mx: "auto", position: "relative", zIndex: 1 }}
        >
          {/* ── HEADER BAR ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={0}
          >
            <GlassCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <motion.div
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.96 }}
                >
                  <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                      width: 40,
                      height: 40,
                      background: T.blueGlass,
                      border: `1.5px solid ${T.borderBlue}`,
                      color: T.blue,
                      borderRadius: "12px",
                      "&:hover": { background: "rgba(21,101,192,0.14)" },
                    }}
                  >
                    <FaArrowLeft size={14} />
                  </IconButton>
                </motion.div>
                <Box flex={1}>
                  <Typography
                    sx={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: { xs: "1.15rem", md: "1.35rem" },
                      color: T.text,
                      letterSpacing: "-0.4px",
                      lineHeight: 1.1,
                    }}
                  >
                    My Profile
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: T.textMuted,
                      fontFamily: T.font,
                    }}
                  >
                    Manage your personal information
                  </Typography>
                </Box>
                <Chip
                  label={`ID: ${code}`}
                  size="small"
                  sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    background: T.blueGlass,
                    color: T.blue,
                    border: `1px solid ${T.borderBlue}`,
                    borderRadius: "8px",
                    height: 26,
                    letterSpacing: "0.3px",
                  }}
                />
              </Stack>
            </GlassCard>
          </motion.div>

          {/* ── ALERT ── */}
          <AnimatePresence>
            {alert.message && (
              <motion.div
                key="alert"
                initial={{ opacity: 0, y: -12, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ marginBottom: 16 }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    borderRadius: "14px",
                    background:
                      alert.type === "success"
                        ? "rgba(5,150,105,0.09)"
                        : "rgba(220,38,38,0.08)",
                    border: `1px solid ${
                      alert.type === "success"
                        ? "rgba(5,150,105,0.22)"
                        : "rgba(220,38,38,0.2)"
                    }`,
                    backdropFilter: "blur(12px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background:
                          alert.type === "success"
                            ? "rgba(5,150,105,0.12)"
                            : "rgba(220,38,38,0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {alert.type === "success" ? (
                        <FaCheck size={10} color={T.success} />
                      ) : (
                        <FaExclamationTriangle size={10} color={T.error} />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.83rem",
                        fontWeight: 600,
                        color: alert.type === "success" ? T.success : T.error,
                        fontFamily: T.font,
                      }}
                    >
                      {alert.message}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => setAlert({})}
                    sx={{ color: T.textMuted, width: 24, height: 24 }}
                  >
                    <FaTimes size={10} />
                  </IconButton>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── PROFILE HERO CARD ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
          >
            <GlassCard sx={{ mb: 3 }}>
              {/* Hero Banner */}
              <Box
                sx={{
                  height: { xs: 90, md: 110 },
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #1976D2 40%, #7B3FF2 100%)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* decorative circles */}
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "absolute",
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.06)",
                      width: [120, 80, 60][i],
                      height: [120, 80, 60][i],
                      top: ["-30%", "10%", "30%"][i],
                      right: [
                        `${i * 12}%`,
                        `${15 + i * 10}%`,
                        `${8 + i * 15}%`,
                      ][i],
                    }}
                  />
                ))}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(255,255,255,0.015) 20px, rgba(255,255,255,0.015) 21px)",
                  }}
                />
              </Box>

              {/* Avatar section */}
              <Box sx={{ px: { xs: 2.5, md: 3 }, pb: 3 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "center", sm: "flex-end" }}
                  justifyContent="space-between"
                  sx={{ mt: { xs: "-40px", sm: "-44px" }, mb: 2.5 }}
                  spacing={{ xs: 1.5, sm: 0 }}
                >
                  {/* Avatar with upload */}
                  <Box
                    sx={{ position: "relative", flexShrink: 0 }}
                    onMouseEnter={() => setAvatarHover(true)}
                    onMouseLeave={() => setAvatarHover(false)}
                  >
                    <motion.div
                      animate={{ scale: avatarHover && editMode ? 1.04 : 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Avatar
                        src={picPreview || ProfileImage}
                        alt={name}
                        sx={{
                          width: { xs: 82, md: 96 },
                          height: { xs: 82, md: 96 },
                          border: "4px solid rgba(255,255,255,0.9)",
                          boxShadow: "0 8px 28px rgba(21,101,192,0.22)",
                          fontSize: "1.8rem",
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #1976D2, #7B3FF2)",
                          color: "#fff",
                        }}
                      >
                        {!picPreview && name ? getInitials(name) : null}
                      </Avatar>
                    </motion.div>

                    {/* Camera overlay */}
                    <AnimatePresence>
                      {editMode && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.7 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.7 }}
                          transition={{ duration: 0.2 }}
                          style={{ position: "absolute", bottom: 2, right: 2 }}
                        >
                          <label htmlFor="profile-upload">
                            <input
                              accept="image/*"
                              id="profile-upload"
                              type="file"
                              hidden
                              onChange={handleProfilePicChange}
                            />
                            <Tooltip title="Change photo" arrow>
                              <Box
                                component="span"
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: "50%",
                                  background:
                                    "linear-gradient(135deg, #1565C0, #7B3FF2)",
                                  border: "2.5px solid #fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  boxShadow: "0 3px 10px rgba(21,101,192,0.35)",
                                  transition: "transform 0.2s ease",
                                  "&:hover": { transform: "scale(1.12)" },
                                }}
                              >
                                <FaCamera size={11} color="#fff" />
                              </Box>
                            </Tooltip>
                          </label>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>

                  {/* Name/role info */}
                  <Box
                    sx={{
                      flex: 1,
                      px: { xs: 0, sm: 2.5 },
                      textAlign: { xs: "center", sm: "left" },
                      mt: { sm: 1 },
                    
                      position: "relative", // make it a positioning context
                      zIndex: 2, // lift above background overlays
                    }}
                  >
                    <Typography
                      sx={{
                        // fontFamily: "'Playfair Display', serif",
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        color: "white",
                        letterSpacing: "-0.3px",
                        lineHeight: 1.2,
                      }}
                    >
                      {name || "Your Name"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: T.textSub,
                        fontFamily: T.font,
                        mt: 0.25,
                      }}
                    >
                      @{username || "username"}
                    </Typography>
                    {isInst && (
                      <Chip
                        label="Institute"
                        size="small"
                        icon={
                          <FaSchool
                            size={9}
                            style={{ marginLeft: 8, color: T.purple }}
                          />
                        }
                        sx={{
                          mt: 0.75,
                          height: 20,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          background: "rgba(123,63,242,0.1)",
                          color: T.purple,
                          border: "1px solid rgba(123,63,242,0.2)",
                          borderRadius: "6px",
                          fontFamily: T.font,
                        }}
                      />
                    )}
                  </Box>

                  {/* Edit button */}
                  {!editMode && (
                    <motion.div
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <PillBtn
                        onClick={() => setEditMode(true)}
                        icon={<FaEdit />}
                        color={T.blue}
                      >
                        Edit Profile
                      </PillBtn>
                    </motion.div>
                  )}
                </Stack>

                {/* Divider */}
                <Box
                  sx={{ height: "1px", background: "rgba(0,0,0,0.05)", mb: 3 }}
                />

                {/* Form Fields */}
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box flex={1}>
                      <FieldLabel>Username</FieldLabel>
                      <GlassInput
                        value={username}
                        placeholder="Enter username"
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={!editMode}
                        icon={<FaAtlas />}
                      />
                    </Box>
                    <Box flex={1}>
                      <FieldLabel>Full Name</FieldLabel>
                      <GlassInput
                        value={name}
                        placeholder="Enter full name"
                        onChange={(e) => setName(e.target.value)}
                        disabled={!editMode}
                        icon={<FaUser />}
                      />
                    </Box>
                  </Stack>

                  <Box>
                    <FieldLabel>Address</FieldLabel>
                    <GlassInput
                      value={address}
                      placeholder="Enter your address"
                      onChange={(e) => setAddress(e.target.value)}
                      disabled={!editMode}
                      icon={<FaMapMarkerAlt />}
                    />
                  </Box>

                  {/* Institute toggle */}
                  <motion.div
                    animate={{
                      borderColor: isInst
                        ? "rgba(123,63,242,0.25)"
                        : "rgba(0,0,0,0.07)",
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "14px",
                        background: isInst
                          ? "rgba(123,63,242,0.05)"
                          : "rgba(245,247,250,0.7)",
                        border: `1.5px solid ${
                          isInst ? "rgba(123,63,242,0.2)" : "rgba(0,0,0,0.06)"
                        }`,
                        transition: "all 0.25s ease",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "10px",
                              background: isInst
                                ? "linear-gradient(135deg, rgba(123,63,242,0.15), rgba(168,85,247,0.2))"
                                : "rgba(100,116,139,0.08)",
                              border: `1px solid ${
                                isInst ? "rgba(123,63,242,0.2)" : "transparent"
                              }`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.25s ease",
                            }}
                          >
                            <FaSchool
                              size={14}
                              color={isInst ? T.purple : T.textMuted}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: T.text,
                                fontSize: "0.87rem",
                                fontFamily: T.font,
                              }}
                            >
                              Switch to an Institution
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: T.textMuted,
                                fontFamily: T.font,
                              }}
                            >
                              Enable institute-level features
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          checked={isInst}
                          onChange={(e) => setIsInst(e.target.checked)}
                          disabled={!editMode}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: T.purple,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              { backgroundColor: T.purple },
                          }}
                        />
                      </Stack>
                    </Box>
                  </motion.div>

                  {/* Signature upload */}
                  <Box>
                    <FieldLabel>Signature</FieldLabel>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: "14px",
                        border: "1.5px dashed rgba(21,101,192,0.2)",
                        background: "rgba(245,248,255,0.6)",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                        minHeight: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AnimatePresence>
                        {isRemovingBg && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{
                              position: "absolute",
                              inset: 0,
                              background: "rgba(255,255,255,0.88)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 8,
                              backdropFilter: "blur(4px)",
                              zIndex: 2,
                            }}
                          >
                            <CircularProgress
                              size={22}
                              sx={{ color: T.blue }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: T.textSub,
                                fontFamily: T.font,
                              }}
                            >
                              Removing background…
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {signPreview ? (
                        <img
                          src={signPreview}
                          alt="Signature"
                          style={{
                            maxHeight: 90,
                            maxWidth: "100%",
                            objectFit: "contain",
                            borderRadius: 6,
                          }}
                        />
                      ) : (
                        <Stack alignItems="center" spacing={0.5}>
                          <FaSignature size={22} color={T.textMuted} />
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: T.textMuted,
                              fontFamily: T.font,
                            }}
                          >
                            No signature uploaded
                          </Typography>
                        </Stack>
                      )}
                    </Box>

                    <AnimatePresence>
                      {editMode && (
                        <motion.div
                          variants={slideDown}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                        >
                          <label htmlFor="sign-upload">
                            <input
                              accept="image/*"
                              id="sign-upload"
                              type="file"
                              hidden
                              onChange={handleSignatureUpload}
                            />
                            <Button
                              component="span"
                              variant="outlined"
                              startIcon={<FaSignature size={12} />}
                              fullWidth
                              sx={{
                                mt: 1.5,
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                fontFamily: T.font,
                                borderColor: T.borderBlue,
                                color: T.blue,
                                background: "rgba(255,255,255,0.6)",
                                "&:hover": {
                                  borderColor: T.blue,
                                  background: T.blueGlass,
                                },
                              }}
                            >
                              Upload Signature (Auto Background Removal)
                            </Button>
                          </label>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                </Stack>
              </Box>

              {/* Footer actions */}
              <AnimatePresence>
                {editMode && (
                  <motion.div
                    variants={slideDown}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Box
                      sx={{
                        px: 3,
                        py: 2.5,
                        borderTop: "1px solid rgba(0,0,0,0.05)",
                        background: "rgba(248,250,255,0.7)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1.5,
                      }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <Button
                          onClick={() => setEditMode(false)}
                          sx={{
                            borderRadius: "100px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.83rem",
                            color: T.textSub,
                            fontFamily: T.font,
                            px: 2.5,
                            "&:hover": { background: "rgba(0,0,0,0.04)" },
                          }}
                        >
                          Cancel
                        </Button>
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        <PillBtn
                          onClick={handleSave}
                          disabled={isLoading}
                          loading={isLoading}
                          icon={<FaSave />}
                          color={T.blue}
                          size="medium"
                        >
                          Save Changes
                        </PillBtn>
                      </motion.div>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          </motion.div>

          {/* ── CHANGE PASSWORD CARD ── */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <GlassCard>
              <SectionHeader
                icon={<FaKey size={14} color="#fff" />}
                color={T.purple}
                title="Change Password"
                subtitle="Update your account security"
                action={
                  !changePasswordMode && (
                    <motion.div
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <PillBtn
                        onClick={() => setChangePasswordMode(true)}
                        variant="outline"
                        icon={<FaShieldAlt />}
                        color={T.purple}
                      >
                        Change
                      </PillBtn>
                    </motion.div>
                  )
                }
              />

              <AnimatePresence>
                {changePasswordMode && (
                  <motion.div
                    variants={slideDown}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    <Box sx={{ p: 3 }}>
                      <Stack spacing={2.5}>
                        <Box>
                          <FieldLabel>Current Password</FieldLabel>
                          <GlassInput
                            value={oldPassword}
                            placeholder="Enter current password"
                            onChange={(e) => setOldPassword(e.target.value)}
                            type={showOld ? "text" : "password"}
                            icon={<FaKey />}
                            accentColor={T.purple}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowOld((v) => !v)}
                                  sx={{
                                    color: T.textMuted,
                                    "&:hover": { color: T.purple },
                                  }}
                                >
                                  {showOld ? (
                                    <FaEyeSlash size={13} />
                                  ) : (
                                    <FaEye size={13} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        </Box>

                        <Box>
                          <FieldLabel>New Password</FieldLabel>
                          <GlassInput
                            value={newPassword}
                            placeholder="Enter new password"
                            onChange={(e) => setNewPassword(e.target.value)}
                            type={showNew ? "text" : "password"}
                            icon={<FaShieldAlt />}
                            accentColor={T.purple}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowNew((v) => !v)}
                                  sx={{
                                    color: T.textMuted,
                                    "&:hover": { color: T.purple },
                                  }}
                                >
                                  {showNew ? (
                                    <FaEyeSlash size={13} />
                                  ) : (
                                    <FaEye size={13} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        </Box>

                        {/* Password strength hint */}
                        {newPassword.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                            >
                              {[1, 2, 3, 4].map((level) => (
                                <Box
                                  key={level}
                                  sx={{
                                    height: 3,
                                    flex: 1,
                                    borderRadius: 4,
                                    transition: "background 0.3s ease",
                                    background:
                                      newPassword.length < 6 && level <= 1
                                        ? "#EF4444"
                                        : newPassword.length < 10 && level <= 2
                                        ? "#F59E0B"
                                        : newPassword.length >= 10 && level <= 3
                                        ? "#10B981"
                                        : newPassword.length >= 14 && level <= 4
                                        ? "#10B981"
                                        : "rgba(0,0,0,0.08)",
                                  }}
                                />
                              ))}
                              <Typography
                                sx={{
                                  fontSize: "0.68rem",
                                  color: T.textMuted,
                                  fontFamily: T.font,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {newPassword.length < 6
                                  ? "Weak"
                                  : newPassword.length < 10
                                  ? "Fair"
                                  : "Strong"}
                              </Typography>
                            </Stack>
                          </motion.div>
                        )}

                        <Stack direction="row" spacing={1.5} pt={0.5}>
                          <motion.div
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            <PillBtn
                              onClick={handlePasswordChange}
                              disabled={isLoading}
                              loading={isLoading}
                              icon={<FaCheck />}
                              color={T.purple}
                              size="medium"
                            >
                              Update Password
                            </PillBtn>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            <Button
                              onClick={() => {
                                setChangePasswordMode(false);
                                setOldPassword("");
                                setNewPassword("");
                              }}
                              sx={{
                                borderRadius: "100px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.83rem",
                                color: T.textSub,
                                fontFamily: T.font,
                                px: 2.5,
                                "&:hover": { background: "rgba(0,0,0,0.04)" },
                              }}
                            >
                              Cancel
                            </Button>
                          </motion.div>
                        </Stack>
                      </Stack>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </GlassCard>
          </motion.div>
        </Box>
      </Box>
    </>
  );
};

export default InstProfilePage;